"use client";

import { useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";
import axios from "axios";
const ReactApexChart = dynamic(() => import("react-apexcharts"), {
  ssr: false,
});

interface DownloadData {
  name: string;
  data: number[];
}

interface ComparisonChartProps {
  packages: string[];
  timeRange: string;
}

export default function ComparisonChart({
  packages,
  timeRange,
}: ComparisonChartProps) {
  const [chartData, setChartData] = useState<{
    series: DownloadData[];
    categories: string[];
  }>({ series: [], categories: [] });
  const [mounted, setMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      setMounted(true);
      if (process.env.NODE_ENV === 'development') {
        console.log("chart mounted");
      }
    }
  }, []);

  useEffect(() => {
    if (!mounted || packages.length === 0) return;

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const end = new Date();
        const start = new Date();
        switch (timeRange) {
          case "6m":
            start.setMonth(end.getMonth() - 6);
            break;
          case "1y":
            start.setFullYear(end.getFullYear() - 1);
            break;
          case "3y":
            start.setFullYear(end.getFullYear() - 3);
            break;
          case "5y":
            start.setFullYear(end.getFullYear() - 5);
            break;
          default:
            start.setFullYear(end.getFullYear() - 1);
        }

        const startDate = start.toISOString().split("T")[0];
        const endDate = end.toISOString().split("T")[0];

        const response = await axios.get("/api/packages/trends-all", {
          params: {
            packages: packages.join(","),
            start: startDate,
            end: endDate,
          },
          signal: abortControllerRef.current?.signal,
        });

        const data = response.data;
        if (!Array.isArray(data) || data.length === 0) {
          throw new Error("Invalid data format");
        }

        // Transform data for ApexCharts
        const categories = data.map((point) => {
          const date = new Date(point.date);
          return date.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          });
        });

        const series = packages.map((pkg) => ({
          name: pkg,
          data: data.map((point) => point[pkg] || 0),
        }));

        if (process.env.NODE_ENV === 'development') {
          console.log("series", series);
          console.log("categories", categories);
        }

        setChartData({ series, categories });
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Error fetching download data:", error);
          setChartData({ series: [], categories: [] });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [packages, timeRange, mounted]);

  if (!mounted) return null;

  const chartOptions = {
    chart: {
      type: "line" as const,
      height: 400,
      width: "100%",
      animations: {
        enabled: true,
        easing: "linear" as const,
        dynamicAnimation: {
          speed: 500,
        },
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
        export: {
          csv: {
            filename: `npmstar.com-${packages.join("-vs-")}-downloads`,
            columnDelimiter: ',',
            headerCategory: 'Date',
            headerValue: 'Downloads',
          },
          svg: {
            filename: `npmstar.com-${packages.join("-vs-")}-chart`,
          },
          png: {
            filename: `npmstar.com-${packages.join("-vs-")}-chart`,
          },
        },
      },
      background: "transparent",
    },
    stroke: {
      width: 2,
      curve: "smooth" as const,
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        show: true,
        rotate: -45,
        rotateAlways: false,
        style: {
          fontSize: '12px',
          fontFamily: 'inherit',
        },
      },
      axisBorder: {
        show: true,
      },
      axisTicks: {
        show: true,
      },
    },
    yaxis: {
      labels: {
        formatter: function (val: any) {
          if (val === null || val === undefined) return "0";
          const num = parseFloat(val);
          if (isNaN(num)) return "0";
          if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
          if (num >= 1000) return (num / 1000).toFixed(0) + "K";
          return num.toFixed(0);
        },
        style: {
          fontSize: '12px',
          fontFamily: 'inherit',
        },
      },
    },
    legend: {
      show: true,
      position: "top" as const,
      fontSize: '13px',
      fontFamily: 'inherit',
      markers: {
        width: 12,
        height: 12,
        radius: 12,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val: any) {
          if (val === null || val === undefined) return "0";
          const num = parseFloat(val);
          if (isNaN(num)) return "0";
          return num.toLocaleString();
        },
      },
      style: {
        fontSize: '12px',
        fontFamily: 'inherit',
      },
    },
    grid: {
      borderColor: '#E5E7EB',
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      },
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          height: 300
        },
        legend: {
          position: 'bottom'
        }
      }
    }]
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-[#E5E5E5]">
      <h3 className="text-xl font-semibold mb-4">Download Trends</h3>
      <div className="h-[400px] w-full">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-gray-500">Loading download data...</div>
          </div>
        ) : chartData.series.length === 0 || chartData.categories.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-gray-500">No data available</div>
          </div>
        ) : (
          <div className="h-full w-full">
            <ReactApexChart
              key={`${packages.join("-")}-${timeRange}`}
              options={chartOptions}
              series={chartData.series}
              type="line"
              height="100%"
              width="100%"
            />
          </div>
        )}
      </div>
    </div>
  );
}
